/**
 * `client-login-binding` (docs/43 §3.2, §6.1; crossmarc/steward-app#2699 slice 1): a login's
 * binding to one client record. Key (userId, firm), value the `advisor-client` id, plus a
 * `source` recording HOW it was created. It is the authority for every client write (§6.1), and
 * it replaces both address-shaped bindings (`DRAFTOWNER#`/`CLAIMEMAIL#` and `ONBSIGNUPGRANT`).
 *
 * Registered in `STEWARD_ADVISOR_FIRM_TYPE_DEFS` (via `CLIENT_RECORD_TYPE_DEFS`), so the next
 * pack seed writes the CTYPE. Since slice 3a appcore's member plane creates and deletes rows of this
 * type for the invitation-grant path; since slice 3b the intake writer creates and deletes them
 * over two `/system` routes (the `bind` and `unbind` messages below). Nothing outside appcore
 * reads them. The instance id format below is appcore's to accept.
 *
 * ## Who may touch it
 *
 * Both its capabilities (`clientRecordAccess.ts`) are held by NO role: the write is in
 * `SYSTEM_ONLY_WRITE_CAPABILITIES` and the read in `SYSTEM_ONLY_READ_CAPABILITIES`, and both are
 * armed on the type, so appcore's generic member-plane routes refuse it to everyone but a `*:*`
 * holder, workspace admins included (appcore#603). The write half is pinned wildcard-aware by
 * `advisorClient.test.ts`, both halves by `clientRecordAccess.test.ts`. Only appcore's own client
 * route and the intake writer (over `/system/*`) touch it. Same `appcore#313` shadow residual as
 * `ADVISOR_CLIENT_WRITE_CAPABILITY`.
 *
 * ## Never re-pointed
 *
 * A second proof for a different address, arriving while a binding exists, is refused (§6.1).
 * That is writer and IAM logic (slice 3), not schema; the row carries no update path.
 *
 * ⚠️ Open for slice 3 (appcore-side, not decided here): the INSTANCE ID derived from
 * (userId, firm). Appcore must accept it, and it must be deterministic so a second binding for
 * the same (userId, firm) collides rather than duplicates.
 */
import { z } from 'zod';
import type { FoTypeDef } from './index.js';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { ULID_PATTERN } from './intakeMessage.js';
import { CLIENT_LOGIN_BINDING_READ_CAPABILITY, CLIENT_LOGIN_BINDING_WRITE_CAPABILITY } from './clientRecordAccess.js';

export const CLIENT_LOGIN_BINDING_TYPE_CODE = 'client-login-binding';

/** The two fields that key a binding. */
export const CLIENT_LOGIN_BINDING_KEY_FIELDS = ['userId', 'firmWorkspaceId'] as const;

/** How a binding came to exist (§6.1). */
export const CLIENT_LOGIN_BINDING_SOURCES = ['invitation', 'claim'] as const;

export const ClientLoginBindingSchema = z.strictObject({
  userId: z.string().regex(ULID_PATTERN),
  firmWorkspaceId: z.string().regex(ULID_PATTERN),
  clientId: z.string().regex(ULID_PATTERN),
  /** `{ source: 'invitation', invitationId }` or `{ source: 'claim' }`: a cancel deletes only its own invitation's binding. */
  source: z.discriminatedUnion('source', [
    z.strictObject({ source: z.literal('invitation'), invitationId: z.string().regex(ULID_PATTERN) }),
    z.strictObject({ source: z.literal('claim') }),
  ]),
  createdAt: z.iso.datetime().max(30),
});
export type ClientLoginBinding = z.infer<typeof ClientLoginBindingSchema>;

/**
 * The type definition, registered in the advisor-firm pack. `strict`: appcore validates the
 * fields. `source` is a discriminated object in the schema above, so it is stored as two flat
 * fields here (`source`, an enum, and `invitationId`, optional). Appcore can enforce the enum and
 * each field's length from this def, but NOT that `invitationId` is present exactly when
 * `source` is `invitation`: that pairing is slice 3's route code.
 */
export const CLIENT_LOGIN_BINDING_TYPE_DEF: FoTypeDef = {
  typeCode: CLIENT_LOGIN_BINDING_TYPE_CODE,
  name: 'Client Login Binding',
  schemaMode: 'strict',
  auditLevel: 'strict',
  accessControl: { readCapability: CLIENT_LOGIN_BINDING_READ_CAPABILITY, writeCapability: CLIENT_LOGIN_BINDING_WRITE_CAPABILITY },
  fields: [
    { name: 'userId', type: 'string', widget: 'text', required: true, label: 'Login', validation: { maxLength: 26 } },
    { name: 'firmWorkspaceId', type: 'string', widget: 'text', required: true, label: 'Firm workspace', validation: { maxLength: 26 } },
    { name: 'clientId', type: 'string', widget: 'text', required: true, label: 'Client record', validation: { maxLength: 26 } },
    {
      // An enum, so appcore's field list refuses any other value. What it CANNOT express from this
      // def is the pairing: `invitation` requires `invitationId` and `claim` forbids it. That is
      // slice 3's route code (and `ClientLoginBindingSchema` on this side), not appcore enforcing
      // it from this definition.
      name: 'source', type: 'enum', widget: 'select', required: true, label: 'Created by',
      validation: { values: [...CLIENT_LOGIN_BINDING_SOURCES] },
    },
    { name: 'invitationId', type: 'string', widget: 'text', required: false, label: 'Invitation', validation: { maxLength: 26 } },
    { name: 'createdAt', type: 'datetime', widget: 'datetime', required: true, label: 'Created at' },
  ],
  accessPatterns: [],
  presentation: { displayField: 'clientId', listColumns: ['clientId', 'source', 'createdAt'], defaultSort: { field: 'createdAt', direction: 'desc' } },
};

// ── The two writer messages (docs/43 §6.1, slice 3b) ─────────────────────────────────────────

/** The `kind` the intake writer dispatches a binding on, ahead of the draft family's check. */
export const CLIENT_LOGIN_BIND_KIND = 'bind';

/** The `kind` the intake writer dispatches a binding delete on, ahead of the draft family's check. */
export const CLIENT_LOGIN_UNBIND_KIND = 'unbind';

/**
 * "Bind this login to its client record, if the writer's own rows prove it." The message names
 * a login and a firm and NOTHING ELSE: no address, no client id, no source, no invitation. The
 * proof is derived by the writer from the stored draft owner (and, for an invitation, the
 * invitation row and its watermark), so a message cannot be told what to prove. appcore then
 * re-makes the address proof from its own rows before it commits (`PUT .../client-login-bindings/by-user/{userId}`).
 *
 * Sent by the writer's own save path today (inline, without a queue hop) and by the façade's
 * repair trigger from slice 4; the queue form exists so that repair needs no new writer code.
 * Strict, like every message on these queues: an undeclared key is refused, not stripped.
 */
export const ClientLoginBindMessageSchema = z.strictObject({
  kind: z.literal(CLIENT_LOGIN_BIND_KIND),
  v: z.literal(1),
  userId: z.string().regex(ULID_PATTERN),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
});
export type ClientLoginBindMessage = z.infer<typeof ClientLoginBindMessageSchema>;

/**
 * "Remove the bindings an invitation made." Sent by the façade beside `invitation-superseded`
 * whenever an invitation is cancelled (an advisor's cancel, or a client purge's sweep). The
 * writer, not the mailer, performs the delete: it is the only background role with a route onto
 * the type. Deletes only `source: 'invitation'` bindings that carry this invitation id, so a
 * claim binding and another invitation's binding are never touched (appcore enforces both by
 * condition, not only by filter). A delete that finds nothing is a normal outcome.
 */
export const ClientLoginUnbindMessageSchema = z.strictObject({
  kind: z.literal(CLIENT_LOGIN_UNBIND_KIND),
  v: z.literal(1),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  invitationId: z.string().regex(ULID_PATTERN),
});
export type ClientLoginUnbindMessage = z.infer<typeof ClientLoginUnbindMessageSchema>;

/** `undefined` for anything this deploy does not understand, the contract of `parseIntakeDraftMessage`. */
export function parseClientLoginBindMessage(value: unknown): ClientLoginBindMessage | undefined {
  const parsed = ClientLoginBindMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/** {@link parseClientLoginBindMessage}'s contract, for the `unbind` message. */
export function parseClientLoginUnbindMessage(value: unknown): ClientLoginUnbindMessage | undefined {
  const parsed = ClientLoginUnbindMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
